import { useEffect, useId, useState } from "react";
import { AlertCircle, CheckCircle2, Loader2, X, XCircle } from "lucide-react";
import { cn } from "@/lib/cn";
import { usePatchRequest } from "@/hooks/useInternalRequests";

/**
 * #9 S3 — Complete/Result modal (TZ §12.5, паралель TaskCompleteModal-канону).
 * P3 no-silent-close: done → resultText* + resultStatus*; reject → rejectReason*.
 * Опціонально — follow-up задача менеджеру (requester) за результатом.
 */

const RESULT_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "completed", label: "Виконано" },
  { value: "completed_with_changes", label: "Виконано зі змінами" },
  { value: "redirected", label: "Перенаправлено" },
  { value: "duplicate", label: "Дублікат" },
];

export function CompleteRequestModal({
  target, mode, onClose, onDone,
}: {
  target: { id: string; subject: string; requestNumber?: string | null } | null;
  mode: "complete" | "reject";
  onClose: () => void;
  onDone?: () => void;
}) {
  const open = !!target;
  const headingId = useId();
  const patch = usePatchRequest();
  const [resultText, setResultText] = useState("");
  const [resultStatus, setResultStatus] = useState("");
  const [fuOn, setFuOn] = useState(false);
  const [fuTitle, setFuTitle] = useState("");
  const [fuDate, setFuDate] = useState("");
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setResultText(""); setResultStatus(""); setFuOn(false); setFuTitle(""); setFuDate(""); setErr(null);
  }, [open, mode]);

  if (!open || !target) return null;

  const isReject = mode === "reject";
  const canSubmit = resultText.trim() !== "" && (isReject || resultStatus !== "") && !patch.isPending;

  const submit = async () => {
    if (!canSubmit) return;
    try {
      if (isReject) {
        await patch.mutateAsync({ id: target.id, status: "rejected", rejectReason: resultText.trim() });
      } else {
        // resultStatus/followUpTask — canonical-поля (#9); legacy backend їх ігнорує.
        await patch.mutateAsync({
          id: target.id, status: "done", result: resultText.trim(), resultStatus,
          ...(fuOn ? { followUpTask: { title: fuTitle.trim() || undefined, dueDate: fuDate || undefined } } : {}),
        });
      }
      onDone?.();
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Не вдалося зберегти");
    }
  };

  const fieldCls = "w-full rounded-md border border-input bg-background px-3 py-2 text-sm";
  const lblCls = "mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground";

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={onClose} aria-hidden>
      <div role="dialog" aria-modal="true" aria-labelledby={headingId} onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-xl border bg-background shadow-2xl">
        <div className="flex items-center justify-between border-b px-5 py-3.5">
          <h3 id={headingId} className="flex items-center gap-2 text-base font-semibold">
            {isReject
              ? <><XCircle className="h-4 w-4 text-destructive" /> Відхилити запит</>
              : <><CheckCircle2 className="h-4 w-4 text-success-foreground" /> Завершити запит</>}
          </h3>
          <button type="button" onClick={onClose} aria-label="Закрити" className="rounded-md p-1 text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
        </div>

        <div className="space-y-4 px-5 py-4">
          <p className="text-sm text-muted-foreground">
            {target.requestNumber && <span className="mr-1 font-mono text-[12px]">{target.requestNumber}</span>}
            {target.subject}
          </p>

          <div>
            <label className={lblCls}>{isReject ? "Причина відмови" : "Результат"} <span className="text-destructive">*</span></label>
            <textarea rows={4} value={resultText} onChange={(e) => setResultText(e.target.value)} className={fieldCls} autoFocus
              placeholder={isReject ? "Чому запит відхилено…" : "Що зроблено / куди дивитись…"} />
          </div>

          {!isReject && (
            <div>
              <label className={lblCls}>Статус результату <span className="text-destructive">*</span></label>
              <select value={resultStatus} onChange={(e) => setResultStatus(e.target.value)} className={fieldCls}>
                <option value="" disabled>— оберіть —</option>
                {RESULT_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          )}

          {!isReject && (
            <div className="rounded-lg border bg-muted/20 px-3 py-2.5">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={fuOn} onChange={(e) => setFuOn(e.target.checked)} />
                Створити задачу менеджеру за результатом
              </label>
              {fuOn && (
                <div className="mt-2 space-y-2">
                  <input value={fuTitle} onChange={(e) => setFuTitle(e.target.value)} className={fieldCls} placeholder="Назва задачі (авто з номера запиту, якщо порожньо)" />
                  <input type="date" value={fuDate} onChange={(e) => setFuDate(e.target.value)} className={fieldCls} />
                </div>
              )}
            </div>
          )}

          {err && <p role="alert" className="flex items-center gap-1.5 text-xs text-destructive"><AlertCircle className="h-3.5 w-3.5" /> {err}</p>}
        </div>

        <div className="flex justify-end gap-2 border-t px-5 py-3.5">
          <button type="button" onClick={onClose} className="rounded-md border px-3 py-2 text-sm text-muted-foreground hover:text-foreground">Скасувати</button>
          <button type="button" onClick={() => void submit()} disabled={!canSubmit}
            className={cn("inline-flex items-center gap-1.5 rounded-md px-3.5 py-2 text-sm font-semibold text-white",
              isReject ? "bg-destructive hover:bg-destructive/90" : "bg-success-foreground hover:bg-success-foreground/90",
              !canSubmit && "cursor-not-allowed opacity-50")}>
            {patch.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {isReject ? "Відхилити" : "Завершити"}
          </button>
        </div>
      </div>
    </div>
  );
}
